import React, { useCallback, useEffect, useRef, useState } from 'react';
import SpeedTest, { MeasurementConfig } from '@cloudflare/speedtest';
import { Button } from '../ui/button';
import { FormField, ConnectionCheckOptions } from '../../types/form';
import { Wifi, CheckCircle2, XCircle, RefreshCw, Loader2 } from 'lucide-react';

interface ConnectionCheckFieldProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
}

// Rhety palette
const INK = '#26251e';
const ACCENT = '#f54e00';
const PAPER = '#faf9f6';

// Defaults match the backend / task spec.
const DEFAULT_MIN_DOWN = 3;
const DEFAULT_MIN_UP = 2;
const DEFAULT_MAX_RTT = 250;

// Explicit measurement plan (a scaled version of Cloudflare's default) so we
// know the total number of download/upload samples up front and can render a
// real progress fraction for each direction. Larger byte sizes near the end
// are what let fast (gigabit) links ramp up to their true rate.
const MEASUREMENTS: MeasurementConfig[] = [
  { type: 'latency', numPackets: 1 },
  { type: 'download', bytes: 1e5, count: 1, bypassMinDuration: true },
  { type: 'latency', numPackets: 20 },
  { type: 'download', bytes: 1e5, count: 9 },
  { type: 'download', bytes: 1e6, count: 8 },
  { type: 'upload', bytes: 1e5, count: 8 },
  { type: 'download', bytes: 1e7, count: 6 },
  { type: 'upload', bytes: 1e6, count: 6 },
  { type: 'download', bytes: 2.5e7, count: 4 },
  { type: 'upload', bytes: 1e7, count: 4 },
  { type: 'download', bytes: 1e8, count: 3 },
  { type: 'upload', bytes: 2.5e7, count: 3 },
];
const sumCount = (t: 'download' | 'upload') =>
  MEASUREMENTS.reduce(
    (acc, m) => (m.type === t ? acc + ((m as { count?: number }).count ?? 1) : acc),
    0,
  );
const TOTAL_DOWN_POINTS = sumCount('download');
const TOTAL_UP_POINTS = sumCount('upload');
const HARD_TIMEOUT_MS = 90_000;

type Phase = 'idle' | 'running' | 'done' | 'error';

const ConnectionCheckField: React.FC<ConnectionCheckFieldProps> = ({ field, value, onChange }) => {
  const options = (field.options || {}) as ConnectionCheckOptions;
  const minDownMbps = options.minDownMbps ?? DEFAULT_MIN_DOWN;
  const minUpMbps = options.minUpMbps ?? DEFAULT_MIN_UP;
  const maxRttMs = options.maxRttMs ?? DEFAULT_MAX_RTT;

  const [phase, setPhase] = useState<Phase>('idle');
  const [partial, setPartial] = useState<{ rttMs?: number; downMbps?: number; upMbps?: number }>({});
  const [progress, setProgress] = useState<{ down: number; up: number }>({ down: 0, up: 0 });

  const engineRef = useRef<SpeedTest | null>(null);
  const settledRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const teardown = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    try {
      engineRef.current?.pause();
    } catch {
      /* already stopped */
    }
    engineRef.current = null;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      teardown();
    };
  }, [teardown]);

  const bpsToMbps = (bps?: number) =>
    typeof bps === 'number' && isFinite(bps) ? Math.round((bps / 1e6) * 10) / 10 : undefined;

  const runTest = useCallback(() => {
    teardown();
    settledRef.current = false;
    setPartial({});
    setProgress({ down: 0, up: 0 });
    setPhase('running');

    const engine = new SpeedTest({ autoStart: false, measurements: MEASUREMENTS });
    engineRef.current = engine;

    // Resolve the test exactly once — from onFinish (normal) or onRunningChange
    // /timeout (fallback). A transient onError must NOT settle it.
    const settle = () => {
      if (settledRef.current || !mountedRef.current) return;
      const s = engine.results.getSummary();
      const downMbps = bpsToMbps(s.download) ?? 0;
      const upMbps = bpsToMbps(s.upload) ?? 0;
      const rttMs = typeof s.latency === 'number' ? Math.round(s.latency) : 0;

      // No usable throughput at all → a genuine failure (offline/blocked).
      if (downMbps <= 0 && upMbps <= 0) {
        settledRef.current = true;
        teardown();
        setPhase('error');
        return;
      }

      settledRef.current = true;
      teardown();
      const status: 'pass' | 'fail' =
        downMbps >= minDownMbps && upMbps >= minUpMbps && rttMs <= maxRttMs ? 'pass' : 'fail';
      setPartial({ rttMs, downMbps, upMbps });
      setProgress({ down: 1, up: 1 });
      onChangeRef.current({
        downMbps,
        upMbps,
        rttMs,
        jitterMs: typeof s.jitter === 'number' ? Math.round(s.jitter) : undefined,
        packetLoss: typeof s.packetLoss === 'number' ? s.packetLoss : undefined,
        status,
        source: 'cloudflare',
        timestamp: new Date().toISOString(),
      });
      setPhase('done');
    };

    engine.onResultsChange = () => {
      if (!mountedRef.current || settledRef.current) return;
      const s = engine.results.getSummary();
      setPartial({
        rttMs: typeof s.latency === 'number' ? Math.round(s.latency) : undefined,
        downMbps: bpsToMbps(s.download),
        upMbps: bpsToMbps(s.upload),
      });
      const downPts = engine.results.getDownloadBandwidthPoints().length;
      const upPts = engine.results.getUploadBandwidthPoints().length;
      setProgress({
        down: Math.min(1, downPts / TOTAL_DOWN_POINTS),
        up: Math.min(1, upPts / TOTAL_UP_POINTS),
      });
    };

    engine.onFinish = settle;

    engine.onRunningChange = (running: boolean) => {
      // If the engine stops without a normal finish, decide based on results.
      if (!running) setTimeout(settle, 0);
    };

    // Transient/recoverable errors: record nothing fatal, let it continue. The
    // test still calls onFinish at the end; settle() then judges by results.
    engine.onError = () => {
      /* intentionally non-fatal */
    };

    timeoutRef.current = setTimeout(settle, HARD_TIMEOUT_MS);
    engine.play();
  }, [maxRttMs, minDownMbps, minUpMbps, teardown]);

  const result = phase === 'done' && value ? value : null;

  const Metric: React.FC<{
    label: string;
    actual?: number;
    unit: string;
    requirement: string;
    ok?: boolean;
    progressPct?: number;
    showBar?: boolean;
  }> = ({ label, actual, unit, requirement, ok, progressPct, showBar }) => (
    <div
      className="flex flex-col rounded-lg border p-3"
      style={{ borderColor: `${INK}1a`, backgroundColor: '#ffffff' }}
    >
      <span className="text-xs" style={{ color: `${INK}99` }}>
        {label}
      </span>
      <span className="mt-0.5 text-lg font-semibold" style={{ color: INK }}>
        {actual === undefined ? '—' : `${actual} ${unit}`}
      </span>
      {showBar && (
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full" style={{ backgroundColor: `${INK}14` }}>
          <div
            className="h-full rounded-full transition-all duration-300 ease-out"
            style={{ width: `${Math.round((progressPct ?? 0) * 100)}%`, backgroundColor: ACCENT }}
          />
        </div>
      )}
      <span className="mt-1 flex items-center gap-1 text-xs" style={{ color: `${INK}80` }}>
        {ok === undefined ? null : ok ? (
          <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#166534' }} aria-hidden="true" />
        ) : (
          <XCircle className="w-3.5 h-3.5" style={{ color: '#b91212' }} aria-hidden="true" />
        )}
        {requirement}
      </span>
    </div>
  );

  const downOk = partial.downMbps === undefined ? undefined : partial.downMbps >= minDownMbps;
  const upOk = partial.upMbps === undefined ? undefined : partial.upMbps >= minUpMbps;
  const rttOk = partial.rttMs === undefined ? undefined : partial.rttMs <= maxRttMs;
  const running = phase === 'running';

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: `${INK}1f`, backgroundColor: PAPER, color: INK }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wifi className="w-5 h-5" style={{ color: ACCENT }} aria-hidden="true" />
          <span className="font-medium" style={{ color: INK }}>
            Connection check
          </span>
        </div>
        {result && (
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
            style={
              result.status === 'pass'
                ? { backgroundColor: '#16653414', color: '#166534' }
                : { backgroundColor: '#b9121214', color: '#b91212' }
            }
          >
            {result.status === 'pass' ? (
              <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
            ) : (
              <XCircle className="w-3.5 h-3.5" aria-hidden="true" />
            )}
            {result.status === 'pass' ? 'Passed' : 'Failed'}
          </span>
        )}
      </div>

      <p className="mt-1 text-xs" style={{ color: `${INK}99` }}>
        We measure your latency, download, and upload speed to confirm a stable connection.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Metric label="Latency" actual={partial.rttMs} unit="ms" requirement={`≤ ${maxRttMs} ms`} ok={rttOk} />
        <Metric
          label="Download"
          actual={partial.downMbps}
          unit="Mbps"
          requirement={`≥ ${minDownMbps} Mbps`}
          ok={downOk}
          showBar={running}
          progressPct={progress.down}
        />
        <Metric
          label="Upload"
          actual={partial.upMbps}
          unit="Mbps"
          requirement={`≥ ${minUpMbps} Mbps`}
          ok={upOk}
          showBar={running}
          progressPct={progress.up}
        />
      </div>

      {running && (
        <div className="mt-3 flex items-center gap-2 text-sm" style={{ color: `${INK}b0` }} role="status">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: ACCENT }} aria-hidden="true" />
          <span>Running speed test… this takes about 20–30 seconds.</span>
        </div>
      )}

      {phase === 'error' && (
        <div
          className="mt-3 flex items-start gap-2 rounded-lg p-3 text-sm"
          style={{ backgroundColor: '#b9121210', color: '#b91212' }}
          role="alert"
        >
          <XCircle className="mt-0.5 w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <span>The connection test could not be completed. Check your internet connection and try again.</span>
        </div>
      )}

      <div className="mt-4">
        {phase === 'idle' && (
          <Button type="button" onClick={runTest} style={{ backgroundColor: ACCENT, color: PAPER }}>
            <Wifi className="mr-2 w-4 h-4" aria-hidden="true" />
            Run test
          </Button>
        )}
        {running && (
          <Button type="button" disabled style={{ backgroundColor: ACCENT, color: PAPER }}>
            <Loader2 className="mr-2 w-4 h-4 animate-spin" aria-hidden="true" />
            Running…
          </Button>
        )}
        {(phase === 'done' || phase === 'error') && (
          <Button type="button" variant="outline" onClick={runTest} style={{ borderColor: `${INK}33`, color: INK }}>
            <RefreshCw className="mr-2 w-4 h-4" aria-hidden="true" />
            {phase === 'error' ? 'Retry' : 'Run again'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ConnectionCheckField;
